import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import Modal from '../components/Modal';
import './Dashboard.css';

// Format 24-hour time to 12-hour format
const formatTime12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${ampm}`;
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [weather, setWeather] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [requestsTab, setRequestsTab] = useState('new'); // 'new' or 'all'
  const [orderTab, setOrderTab] = useState('current'); // 'current' or 'history'
  const [editingOrderItemId, setEditingOrderItemId] = useState(null);
  const [editOrderData, setEditOrderData] = useState({ item_name: '', quantity: '', vendor: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [orderFormData, setOrderFormData] = useState({
    item_name: '',
    quantity: '',
    vendor: '',
    notes: ''
  });
  const [showTodayModal, setShowTodayModal] = useState(false);
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [showUnpaidModal, setShowUnpaidModal] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [selectedDashboardInvoice, setSelectedDashboardInvoice] = useState(null);
  const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, todayRes, upcomingRes, invoicesRes, orderItemsRes, incomingReqRes, weatherRes] = await Promise.all([
        fetch('/api/dashboard/stats'),
        fetch('/api/dashboard/today'),
        fetch('/api/dashboard/upcoming'),
        fetch('/api/dashboard/recent-invoices'),
        fetch('/api/order-items?show_ordered=true'),
        fetch('/api/incoming-requests'), // Show all requests (pending, contacted, and converted)
        fetch('/api/weather/current')
      ]);

      const [statsData, todayData, upcomingData, invoicesData, orderItemsData, incomingReqData, weatherData] = await Promise.all([
        statsRes.json(),
        todayRes.json(),
        upcomingRes.json(),
        invoicesRes.json(),
        orderItemsRes.json(),
        incomingReqRes.json(),
        weatherRes.json()
      ]);

      setStats(statsData);
      setTodayAppointments(todayData.appointments || todayData);
      setTodayEvents(todayData.events || []);
      setUpcomingAppointments(upcomingData.appointments || upcomingData);
      setUpcomingEvents(upcomingData.events || []);
      setRecentInvoices(invoicesData);
      setOrderItems(orderItemsData);
      setIncomingRequests(incomingReqData);
      setWeather(weatherData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      await fetch(`/api/appointments/${appointmentId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAddOrderItem = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/order-items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderFormData)
      });

      if (response.ok) {
        setOrderFormData({ item_name: '', quantity: '', vendor: '', notes: '' });
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error adding order item:', error);
    }
  };

  const handleDeleteOrderItem = async (itemId) => {
    try {
      await fetch(`/api/order-items/${itemId}`, {
        method: 'DELETE'
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting order item:', error);
    }
  };

  const handleToggleOrdered = async (itemId, isOrdered) => {
    try {
      await fetch(`/api/order-items/${itemId}/ordered`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_ordered: !isOrdered })
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating order item:', error);
    }
  };

  const handleUpdateOrderItem = async (itemId) => {
    try {
      const response = await fetch(`/api/order-items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editOrderData)
      });
      if (response.ok) {
        setEditingOrderItemId(null);
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error updating order item:', error);
    }
  };

  const handleMarkRequestContacted = async (requestId) => {
    try {
      await fetch(`/api/incoming-requests/${requestId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'contacted' })
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating request:', error);
    }
  };

  const handleConvertToCustomer = async (requestId) => {
    try {
      const response = await fetch(`/api/incoming-requests/${requestId}/convert`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Successfully converted to customer! Customer ID: ${data.customer.id}`);
        fetchDashboardData();
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        alert(`Failed to convert to customer: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error converting request:', error);
      alert('Failed to convert to customer: Network error');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) {
      return;
    }

    try {
      await fetch(`/api/incoming-requests/${requestId}`, {
        method: 'DELETE'
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting request:', error);
    }
  };

  const handleDeleteAllContacted = async () => {
    if (!window.confirm('Are you sure you want to delete ALL contacted requests? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch('/api/incoming-requests/bulk/contacted', {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Deleted ${data.deletedCount} contacted request(s)`);
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error bulk deleting requests:', error);
      alert('Failed to delete contacted requests');
    }
  };

  const fetchUnpaidInvoices = async () => {
    try {
      // Fetch all invoices that aren't paid or cancelled
      const draftRes = await fetch('/api/invoices?status=draft');
      const draft = await draftRes.json();
      const sentRes = await fetch('/api/invoices?status=sent');
      const sent = await sentRes.json();
      const overdueRes = await fetch('/api/invoices?status=overdue');
      const overdue = await overdueRes.json();
      setUnpaidInvoices([...draft, ...sent, ...overdue].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Error fetching unpaid invoices:', error);
    }
  };

  const handleShowUnpaidModal = () => {
    fetchUnpaidInvoices();
    setShowUnpaidModal(true);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1 className="page-header__title">Dashboard</h1>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        {/* Weather Widget */}
        {weather && (
          <div className={`stat-card weather-card ${weather.temp !== null ? (weather.isGoodForDetailing ? 'weather-card--good' : 'weather-card--warning') : 'weather-card--setup'}`}>
            <div className="weather-card__header">
              <div className="weather-card__temp">
                {weather.temp !== null ? `${weather.temp}°F` : '??°'}
              </div>
              {weather.icon && (
                <img
                  src={`https://openweathermap.org/img/wn/${weather.icon}@2x.png`}
                  alt={weather.description}
                  className="weather-card__icon"
                />
              )}
            </div>
            <div className="weather-card__details">
              <div className="weather-card__description">{weather.description || 'Weather not configured'}</div>
              <div className="weather-card__message">{weather.message}</div>
            </div>
          </div>
        )}

        <div className="stat-card stat-card--clickable" onClick={() => setShowTodayModal(true)}>
          <div className="stat-card__icon stat-card__icon--gold">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="stat-card__content">
            <span className="stat-card__value">{(stats?.appointments_today || 0) + (stats?.events_today || 0)}</span>
            <span className="stat-card__label">Today's Schedule</span>
          </div>
        </div>

        <div className="stat-card stat-card--clickable stat-card--pulse" onClick={() => setShowRequestsModal(true)}>
          <div className="stat-card__icon stat-card__icon--purple">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <circle cx="9" cy="10" r="1" fill="currentColor" />
              <circle cx="12" cy="10" r="1" fill="currentColor" />
              <circle cx="15" cy="10" r="1" fill="currentColor" />
            </svg>
          </div>
          <div className="stat-card__content">
            <span className="stat-card__value">{incomingRequests.filter(r => r.status === 'pending').length}</span>
            <span className="stat-card__label">New Requests</span>
          </div>
        </div>

        <div className="stat-card stat-card--clickable" onClick={() => setShowOrderModal(true)}>
          <div className="stat-card__icon stat-card__icon--blue">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
              <line x1="9" y1="12" x2="15" y2="12" />
              <line x1="9" y1="16" x2="15" y2="16" />
            </svg>
          </div>
          <div className="stat-card__content">
            <span className="stat-card__value">{orderItems.filter(item => !item.is_ordered).length}</span>
            <span className="stat-card__label">Order Form Items</span>
          </div>
        </div>

        <div className="stat-card stat-card--clickable" onClick={() => setShowRevenueModal(true)}>
          <div className="stat-card__icon stat-card__icon--green">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="stat-card__content">
            <span className="stat-card__value">${(stats?.revenue_month || 0).toLocaleString()}</span>
            <span className="stat-card__label">Revenue This Month</span>
          </div>
        </div>

        <div className="stat-card stat-card--clickable" onClick={handleShowUnpaidModal}>
          <div className="stat-card__icon stat-card__icon--orange">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2l-3 2-3-2-3 2-3-2-3 2-3-2z" />
              <path d="M8 10h8" />
              <path d="M8 14h4" />
            </svg>
          </div>
          <div className="stat-card__content">
            <span className="stat-card__value">{stats?.unpaid_invoices?.count || 0}</span>
            <span className="stat-card__label">
              Unpaid (${(stats?.unpaid_invoices?.total || 0).toLocaleString()})
            </span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Today's Schedule */}
        <div className="dashboard-card">
          <div className="dashboard-card__header">
            <h2 className="dashboard-card__title">Today's Schedule</h2>
            <Link to="/appointments" className="dashboard-card__link">View All</Link>
          </div>
          <div className="dashboard-card__content">
            {todayAppointments.length === 0 && todayEvents.length === 0 ? (
              <div className="empty-state empty-state--small">
                <p>Nothing scheduled for today</p>
              </div>
            ) : (
              <div className="appointment-list">
                {todayEvents.map((evt) => (
                  <div key={`event-${evt.id}`} className="appointment-item" style={{ borderLeft: `3px solid ${evt.color || '#C9A227'}`, paddingLeft: '0.75rem' }}>
                    <div className="appointment-item__time">
                      {evt.event_time ? formatTime12Hour(evt.event_time) : 'All Day'}
                    </div>
                    <div className="appointment-item__details">
                      <div className="appointment-item__customer">{evt.title}</div>
                      {evt.description && (
                        <div className="appointment-item__vehicle">{evt.description}</div>
                      )}
                    </div>
                  </div>
                ))}
                {todayAppointments.map((apt) => (
                  <div key={apt.id} className="appointment-item">
                    <div className="appointment-item__time">
                      {formatTime12Hour(apt.scheduled_time)}
                    </div>
                    <div className="appointment-item__details">
                      <div className="appointment-item__customer">
                        {apt.first_name} {apt.last_name}
                      </div>
                      <div className="appointment-item__vehicle">
                        {apt.year} {apt.make} {apt.model}
                      </div>
                      <div className="appointment-item__services">
                        {apt.services?.filter(s => s.name).map(s => s.name).join(', ') || 'No services'}
                      </div>
                    </div>
                    <div className="appointment-item__status">
                      <select
                        className={`status-select status-select--${apt.status}`}
                        value={apt.status}
                        onChange={(e) => handleStatusUpdate(apt.id, e.target.value)}
                      >
                        <option value="scheduled">Scheduled</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Appointments */}
        <div className="dashboard-card">
          <div className="dashboard-card__header">
            <h2 className="dashboard-card__title">Upcoming This Week</h2>
            <Link to="/appointments" className="dashboard-card__link">View All</Link>
          </div>
          <div className="dashboard-card__content">
            {upcomingAppointments.length === 0 && upcomingEvents.length === 0 ? (
              <div className="empty-state empty-state--small">
                <p>Nothing upcoming this week</p>
              </div>
            ) : (
              <div className="upcoming-list">
                {/* Merge and sort appointments and events by date/time */}
                {[
                  ...upcomingAppointments.map(apt => ({ ...apt, _type: 'appointment', _sortDate: apt.scheduled_date, _sortTime: apt.scheduled_time })),
                  ...upcomingEvents.map(evt => ({ ...evt, _type: 'event', _sortDate: evt.event_date, _sortTime: evt.event_time || '00:00' }))
                ].sort((a, b) => `${a._sortDate} ${a._sortTime}`.localeCompare(`${b._sortDate} ${b._sortTime}`))
                .map((item) => (
                  item._type === 'event' ? (
                    <div key={`event-${item.id}`} className="upcoming-item" style={{ borderLeft: `3px solid ${item.color || '#C9A227'}`, paddingLeft: '0.75rem' }}>
                      <div className="upcoming-item__date">
                        <span className="upcoming-item__day">
                          {format(new Date(item.event_date), 'EEE')}
                        </span>
                        <span className="upcoming-item__date-num">
                          {format(new Date(item.event_date), 'd')}
                        </span>
                      </div>
                      <div className="upcoming-item__details">
                        <div className="upcoming-item__customer">{item.title}</div>
                        <div className="upcoming-item__time">
                          {item.event_time ? formatTime12Hour(item.event_time) : 'All Day'}
                        </div>
                      </div>
                      <span className="badge" style={{ backgroundColor: item.color || '#C9A227', color: '#0a0a0a' }}>Event</span>
                    </div>
                  ) : (
                    <div key={`apt-${item.id}`} className="upcoming-item">
                      <div className="upcoming-item__date">
                        <span className="upcoming-item__day">
                          {format(new Date(item.scheduled_date), 'EEE')}
                        </span>
                        <span className="upcoming-item__date-num">
                          {format(new Date(item.scheduled_date), 'd')}
                        </span>
                      </div>
                      <div className="upcoming-item__details">
                        <div className="upcoming-item__customer">
                          {item.first_name} {item.last_name}
                        </div>
                        <div className="upcoming-item__time">
                          {formatTime12Hour(item.scheduled_time)} - {item.year} {item.make} {item.model}
                        </div>
                      </div>
                      <span className={`badge badge--${item.status}`}>
                        {item.status.replace('_', ' ')}
                      </span>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Invoices */}
        <div className="dashboard-card">
          <div className="dashboard-card__header">
            <h2 className="dashboard-card__title">Recent Invoices</h2>
            <Link to="/invoices" className="dashboard-card__link">View All</Link>
          </div>
          <div className="dashboard-card__content">
            {recentInvoices.length === 0 ? (
              <div className="empty-state empty-state--small">
                <p>No invoices yet</p>
              </div>
            ) : (
              <div className="invoice-list">
                {recentInvoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="invoice-item"
                    onClick={() => { setSelectedDashboardInvoice(inv); setShowInvoiceDetailModal(true); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="invoice-item__info">
                      <div className="invoice-item__number">{inv.invoice_number}</div>
                      <div className="invoice-item__customer">
                        {inv.first_name} {inv.last_name}
                      </div>
                    </div>
                    <div className="invoice-item__amount">
                      ${parseFloat(inv.total).toFixed(2)}
                    </div>
                    <span className={`badge badge--${inv.status}`}>
                      {inv.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card">
          <div className="dashboard-card__header">
            <h2 className="dashboard-card__title">Quick Actions</h2>
          </div>
          <div className="dashboard-card__content">
            <div className="quick-actions">
              <Link to="/customers" className="quick-action">
                <div className="quick-action__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="19" y1="8" x2="19" y2="14" />
                    <line x1="22" y1="11" x2="16" y2="11" />
                  </svg>
                </div>
                <span>Add Customer</span>
              </Link>
              <Link to="/appointments" className="quick-action">
                <div className="quick-action__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                    <line x1="12" y1="14" x2="12" y2="18" />
                    <line x1="10" y1="16" x2="14" y2="16" />
                  </svg>
                </div>
                <span>New Appointment</span>
              </Link>
              <Link to="/invoices" className="quick-action">
                <div className="quick-action__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2l-3 2-3-2-3 2-3-2-3 2-3-2z" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="10" y1="10" x2="14" y2="10" />
                  </svg>
                </div>
                <span>Create Invoice</span>
              </Link>
              <Link to="/services" className="quick-action">
                <div className="quick-action__icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </div>
                <span>Manage Services</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Order Form Modal */}
      <Modal
        isOpen={showOrderModal}
        onClose={() => { setShowOrderModal(false); setOrderFormData({ item_name: '', quantity: '', vendor: '', notes: '' }); setOrderTab('current'); }}
        title="Weekly Order Form"
      >
        <form onSubmit={handleAddOrderItem}>
          <div className="form-group">
            <label className="form-label">Item Name *</label>
            <input
              type="text"
              className="form-input"
              value={orderFormData.item_name}
              onChange={(e) => setOrderFormData({ ...orderFormData, item_name: e.target.value })}
              placeholder="e.g., Microfiber towels, Soap, etc."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Quantity</label>
            <input
              type="text"
              className="form-input"
              value={orderFormData.quantity}
              onChange={(e) => setOrderFormData({ ...orderFormData, quantity: e.target.value })}
              placeholder="e.g., 2 cases, 5 gallons, etc."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Preferred Vendor</label>
            <input
              type="text"
              className="form-input"
              value={orderFormData.vendor}
              onChange={(e) => setOrderFormData({ ...orderFormData, vendor: e.target.value })}
              placeholder="e.g., Amazon, Detail King, Autogeek..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              value={orderFormData.notes}
              onChange={(e) => setOrderFormData({ ...orderFormData, notes: e.target.value })}
              rows="2"
              placeholder="Any additional notes..."
            />
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => { setShowOrderModal(false); setOrderFormData({ item_name: '', quantity: '', vendor: '', notes: '' }); setOrderTab('current'); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              Add Item
            </button>
          </div>
        </form>

        {/* Tabs */}
        <div style={{ marginTop: '2rem', display: 'flex', gap: '0', borderBottom: '2px solid #333', marginBottom: '1rem' }}>
          <button
            type="button"
            onClick={() => setOrderTab('current')}
            style={{
              padding: '0.5rem 1.25rem',
              background: orderTab === 'current' ? '#C9A227' : 'transparent',
              color: orderTab === 'current' ? '#000' : '#aaa',
              border: 'none',
              borderRadius: '4px 4px 0 0',
              fontWeight: orderTab === 'current' ? '600' : '400',
              cursor: 'pointer'
            }}
          >
            Current List ({orderItems.filter(i => !i.is_ordered).length})
          </button>
          <button
            type="button"
            onClick={() => setOrderTab('history')}
            style={{
              padding: '0.5rem 1.25rem',
              background: orderTab === 'history' ? '#C9A227' : 'transparent',
              color: orderTab === 'history' ? '#000' : '#aaa',
              border: 'none',
              borderRadius: '4px 4px 0 0',
              fontWeight: orderTab === 'history' ? '600' : '400',
              cursor: 'pointer'
            }}
          >
            History ({orderItems.filter(i => i.is_ordered).length})
          </button>
        </div>

        {orderTab === 'current' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {orderItems.filter(i => !i.is_ordered).length === 0 ? (
              <p style={{ color: '#aaa', fontSize: '0.9rem' }}>No items on the current list.</p>
            ) : orderItems.filter(i => !i.is_ordered).map((item) => (
              <div
                key={item.id}
                style={{
                  padding: '0.75rem',
                  background: '#fff',
                  border: editingOrderItemId === item.id ? '1px solid #C9A227' : '1px solid #ddd',
                  borderRadius: '4px',
                  color: '#1a1a1a'
                }}
              >
                {editingOrderItemId === item.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <input
                      type="text"
                      className="form-input"
                      value={editOrderData.item_name}
                      onChange={(e) => setEditOrderData({ ...editOrderData, item_name: e.target.value })}
                      placeholder="Item name"
                      style={{ color: '#1a1a1a', background: '#f9f9f9' }}
                    />
                    <input
                      type="text"
                      className="form-input"
                      value={editOrderData.quantity}
                      onChange={(e) => setEditOrderData({ ...editOrderData, quantity: e.target.value })}
                      placeholder="Quantity"
                      style={{ color: '#1a1a1a', background: '#f9f9f9' }}
                    />
                    <input
                      type="text"
                      className="form-input"
                      value={editOrderData.vendor}
                      onChange={(e) => setEditOrderData({ ...editOrderData, vendor: e.target.value })}
                      placeholder="Preferred vendor"
                      style={{ color: '#1a1a1a', background: '#f9f9f9' }}
                    />
                    <input
                      type="text"
                      className="form-input"
                      value={editOrderData.notes}
                      onChange={(e) => setEditOrderData({ ...editOrderData, notes: e.target.value })}
                      placeholder="Notes"
                      style={{ color: '#1a1a1a', background: '#f9f9f9' }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                      <button
                        type="button"
                        className="btn btn--primary btn--small"
                        onClick={() => handleUpdateOrderItem(item.id)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn--secondary btn--small"
                        onClick={() => setEditingOrderItemId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1 }}>
                      <strong>{item.item_name}</strong>
                      {item.quantity && <span style={{ marginLeft: '0.5rem', color: '#555' }}>— {item.quantity}</span>}
                      {item.vendor && <div style={{ fontSize: '0.8rem', color: '#777', marginTop: '0.15rem' }}>Vendor: {item.vendor}</div>}
                      {item.notes && <div style={{ fontSize: '0.8rem', color: '#777', marginTop: '0.15rem' }}>{item.notes}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        className="btn btn--secondary btn--small"
                        onClick={() => { setEditingOrderItemId(item.id); setEditOrderData({ item_name: item.item_name, quantity: item.quantity || '', vendor: item.vendor || '', notes: item.notes || '' }); }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn--primary btn--small"
                        onClick={() => handleToggleOrdered(item.id, item.is_ordered)}
                      >
                        Mark Ordered
                      </button>
                      <button
                        type="button"
                        className="btn btn--danger btn--small"
                        onClick={() => handleDeleteOrderItem(item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {orderTab === 'history' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {orderItems.filter(i => i.is_ordered).length === 0 ? (
              <p style={{ color: '#aaa', fontSize: '0.9rem' }}>No order history yet.</p>
            ) : orderItems.filter(i => i.is_ordered).map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: '#f0f0f0',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  color: '#1a1a1a',
                  opacity: 0.8
                }}
              >
                <div style={{ flex: 1 }}>
                  <strong style={{ textDecoration: 'line-through' }}>{item.item_name}</strong>
                  {item.quantity && <span style={{ marginLeft: '0.5rem', color: '#555', textDecoration: 'line-through' }}>— {item.quantity}</span>}
                  {item.vendor && <div style={{ fontSize: '0.8rem', color: '#777', marginTop: '0.15rem' }}>Vendor: {item.vendor}</div>}
                  {item.notes && <div style={{ fontSize: '0.8rem', color: '#777', marginTop: '0.15rem' }}>{item.notes}</div>}
                  {item.ordered_at && (
                    <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                      Ordered: {format(new Date(item.ordered_at), 'MMM d, yyyy')}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => handleToggleOrdered(item.id, item.is_ordered)}
                  >
                    Move Back
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger btn--small"
                    onClick={() => handleDeleteOrderItem(item.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Incoming Requests Modal */}
      <Modal
        isOpen={showRequestsModal}
        onClose={() => { setShowRequestsModal(false); setRequestsTab('new'); }}
        title="Incoming Requests"
      >
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '2px solid #1a1a1a' }}>
          <button
            onClick={() => setRequestsTab('new')}
            style={{
              padding: '0.75rem 1.5rem',
              background: requestsTab === 'new' ? '#667eea' : 'transparent',
              color: requestsTab === 'new' ? 'white' : '#999',
              border: 'none',
              borderBottom: requestsTab === 'new' ? '2px solid #667eea' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: requestsTab === 'new' ? 'bold' : 'normal',
              fontSize: '1rem'
            }}
          >
            Requests ({incomingRequests.filter(r => r.status !== 'contacted').length})
          </button>
          <button
            onClick={() => setRequestsTab('all')}
            style={{
              padding: '0.75rem 1.5rem',
              background: requestsTab === 'all' ? '#667eea' : 'transparent',
              color: requestsTab === 'all' ? 'white' : '#999',
              border: 'none',
              borderBottom: requestsTab === 'all' ? '2px solid #667eea' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: requestsTab === 'all' ? 'bold' : 'normal',
              fontSize: '1rem'
            }}
          >
            All Requests ({incomingRequests.filter(r => r.status === 'contacted').length})
          </button>
        </div>

        {/* Requests Tab */}
        {requestsTab === 'new' && (
          <>
            {incomingRequests.filter(r => r.status !== 'contacted').length === 0 ? (
              <div className="empty-state empty-state--small">
                <p>No pending requests</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {incomingRequests.filter(r => r.status !== 'contacted').map((request) => (
              <div
                key={request.id}
                style={{
                  padding: '1rem',
                  border: '2px solid #667eea',
                  borderRadius: '8px',
                  background: '#2a2a2a',
                  color: '#ffffff'
                }}
              >
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <strong style={{ fontSize: '1.1rem', color: '#667eea' }}>
                      {request.first_name} {request.last_name}
                    </strong>
                    <span className={`badge badge--${request.status}`} style={{ fontSize: '0.75rem' }}>
                      {request.status === 'pending' ? 'New' : request.status === 'contacted' ? 'Contacted' : 'Converted'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
                    Submitted: {format(new Date(request.created_at), 'MMM d, yyyy h:mm a')}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>Phone</div>
                    <div><a href={`tel:${request.phone}`} style={{ color: '#667eea' }}>{request.phone}</a></div>
                  </div>
                  {request.email && (
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>Email</div>
                      <div><a href={`mailto:${request.email}`} style={{ color: '#667eea', fontSize: '0.875rem' }}>{request.email}</a></div>
                    </div>
                  )}
                </div>

                {request.vehicle_type && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>Vehicle Type</div>
                    <div>{request.vehicle_type}</div>
                  </div>
                )}

                {request.message && (
                  <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#1a1a1a', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>Message</div>
                    <div style={{ whiteSpace: 'pre-wrap', color: '#e0e0e0' }}>{request.message}</div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                  {request.status !== 'converted' && (
                    <button
                      type="button"
                      className="btn btn--primary btn--small"
                      onClick={() => handleConvertToCustomer(request.id)}
                    >
                      Convert to Customer
                    </button>
                  )}
                  {request.status !== 'contacted' && (
                    <button
                      type="button"
                      className="btn btn--secondary btn--small"
                      onClick={() => handleMarkRequestContacted(request.id)}
                    >
                      Mark Contacted
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn--danger btn--small"
                    onClick={() => handleDeleteRequest(request.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}

        {/* All Requests Tab (Contacted/Archived) */}
        {requestsTab === 'all' && (
          <>
            {incomingRequests.filter(r => r.status === 'contacted').length === 0 ? (
              <div className="empty-state empty-state--small">
                <p>No contacted requests</p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ color: '#999', margin: 0 }}>
                    These requests have been contacted and can be removed when no longer needed.
                  </p>
                  <button
                    type="button"
                    className="btn btn--danger btn--small"
                    onClick={handleDeleteAllContacted}
                  >
                    Delete All
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {incomingRequests.filter(r => r.status === 'contacted').map((request) => (
                    <div
                      key={request.id}
                      style={{
                        padding: '1rem',
                        border: '2px solid #555',
                        borderRadius: '8px',
                        background: '#2a2a2a',
                        color: '#ffffff',
                        opacity: 0.8
                      }}
                    >
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <strong style={{ fontSize: '1.1rem', color: '#999' }}>
                            {request.first_name} {request.last_name}
                          </strong>
                          <span className="badge badge--contacted" style={{ fontSize: '0.75rem' }}>
                            Contacted
                          </span>
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
                          Submitted: {format(new Date(request.created_at), 'MMM d, yyyy h:mm a')}
                        </div>
                        {request.contacted_at && (
                          <div style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
                            Contacted: {format(new Date(request.contacted_at), 'MMM d, yyyy h:mm a')}
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>Phone</div>
                          <div><a href={`tel:${request.phone}`} style={{ color: '#667eea' }}>{request.phone}</a></div>
                        </div>
                        {request.email && (
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>Email</div>
                            <div><a href={`mailto:${request.email}`} style={{ color: '#667eea', fontSize: '0.875rem' }}>{request.email}</a></div>
                          </div>
                        )}
                      </div>

                      {request.vehicle_type && (
                        <div style={{ marginBottom: '0.75rem' }}>
                          <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>Vehicle Type</div>
                          <div>{request.vehicle_type}</div>
                        </div>
                      )}

                      {request.message && (
                        <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#1a1a1a', borderRadius: '4px' }}>
                          <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>Message</div>
                          <div style={{ whiteSpace: 'pre-wrap', color: '#e0e0e0' }}>{request.message}</div>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                        <button
                          type="button"
                          className="btn btn--danger btn--small"
                          onClick={() => handleDeleteRequest(request.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Modal>

      {/* Today's Schedule Modal */}
      <Modal
        isOpen={showTodayModal}
        onClose={() => setShowTodayModal(false)}
        title="Today's Schedule"
      >
        {todayAppointments.length === 0 && todayEvents.length === 0 ? (
          <div className="empty-state empty-state--small">
            <p>Nothing scheduled for today</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {todayEvents.map((evt) => (
              <div
                key={`event-${evt.id}`}
                style={{
                  padding: '1rem',
                  border: `1px solid ${evt.color || '#C9A227'}`,
                  borderLeft: `4px solid ${evt.color || '#C9A227'}`,
                  borderRadius: '8px',
                  background: '#2a2a2a'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div>
                    <strong style={{ color: evt.color || '#c9a227', fontSize: '1.1rem' }}>
                      {evt.event_time ? formatTime12Hour(evt.event_time) : 'All Day'}
                    </strong>
                    <span style={{ color: '#fff', marginLeft: '0.75rem' }}>
                      {evt.title}
                    </span>
                  </div>
                  <span className="badge" style={{ backgroundColor: evt.color || '#C9A227', color: '#0a0a0a' }}>Event</span>
                </div>
                {evt.location_address && (
                  <div style={{ color: '#888', fontSize: '0.8125rem', marginTop: '0.25rem' }}>
                    {evt.location_address}{evt.location_city && `, ${evt.location_city}`}
                  </div>
                )}
                {evt.description && (
                  <div style={{ marginTop: '0.5rem', color: '#bbb', fontSize: '0.875rem' }}>
                    {evt.description}
                  </div>
                )}
              </div>
            ))}
            {todayAppointments.map((apt) => (
              <div
                key={apt.id}
                style={{
                  padding: '1rem',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  background: '#2a2a2a'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div>
                    <strong style={{ color: '#c9a227', fontSize: '1.1rem' }}>
                      {formatTime12Hour(apt.scheduled_time)}
                    </strong>
                    <span style={{ color: '#fff', marginLeft: '0.75rem' }}>
                      {apt.first_name} {apt.last_name}
                    </span>
                  </div>
                  <span className={`badge badge--${apt.status}`}>{apt.status.replace('_', ' ')}</span>
                </div>
                <div style={{ color: '#999', fontSize: '0.875rem' }}>
                  {apt.year} {apt.make} {apt.model}
                </div>
                {apt.location_address && (
                  <div style={{ color: '#888', fontSize: '0.8125rem', marginTop: '0.25rem' }}>
                    {apt.location_address}{apt.location_city && `, ${apt.location_city}`}
                  </div>
                )}
                <div style={{ marginTop: '0.5rem', color: '#bbb', fontSize: '0.875rem' }}>
                  {apt.services?.filter(s => s.name).map(s => s.name).join(', ') || 'No services'}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="modal__footer">
          <Link to="/appointments" className="btn btn--primary" onClick={() => setShowTodayModal(false)}>
            View Full Calendar
          </Link>
        </div>
      </Modal>

      {/* Revenue This Month Modal */}
      <Modal
        isOpen={showRevenueModal}
        onClose={() => setShowRevenueModal(false)}
        title="Revenue This Month"
      >
        <div style={{ padding: '1rem 0' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '3rem', fontWeight: '700', color: '#10b981' }}>
              ${(stats?.revenue_month || 0).toLocaleString()}
            </div>
            <div style={{ color: '#999', fontSize: '0.875rem' }}>Total Revenue</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ padding: '1rem', background: '#2a2a2a', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#c9a227' }}>
                {stats?.paid_invoices_count || 0}
              </div>
              <div style={{ color: '#999', fontSize: '0.8125rem' }}>Invoices Paid</div>
            </div>
            <div style={{ padding: '1rem', background: '#2a2a2a', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#fff' }}>
                {stats?.appointments_month || 0}
              </div>
              <div style={{ color: '#999', fontSize: '0.8125rem' }}>Appointments</div>
            </div>
          </div>
        </div>
        <div className="modal__footer">
          <Link to="/invoices" className="btn btn--primary" onClick={() => setShowRevenueModal(false)}>
            View All Invoices
          </Link>
        </div>
      </Modal>

      {/* Unpaid Invoices Modal */}
      <Modal
        isOpen={showUnpaidModal}
        onClose={() => setShowUnpaidModal(false)}
        title="Unpaid Invoices"
      >
        {unpaidInvoices.length === 0 ? (
          <div className="empty-state empty-state--small">
            <p>No unpaid invoices</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {unpaidInvoices.map((inv) => (
              <div
                key={inv.id}
                style={{
                  padding: '1rem',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  background: '#2a2a2a',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                    <span style={{ color: '#c9a227', fontFamily: 'monospace', fontWeight: '600' }}>
                      {inv.invoice_number}
                    </span>
                    <span className={`badge badge--${inv.status}`}>{inv.status}</span>
                  </div>
                  <div style={{ color: '#fff' }}>
                    {inv.first_name} {inv.last_name}
                  </div>
                  <div style={{ color: '#888', fontSize: '0.8125rem' }}>
                    {format(new Date(inv.created_at), 'MMM d, yyyy')}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#fff' }}>
                    ${parseFloat(inv.total).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="modal__footer" style={{ marginTop: '1rem' }}>
          <Link to="/invoices" className="btn btn--primary" onClick={() => setShowUnpaidModal(false)}>
            View All Invoices
          </Link>
        </div>
      </Modal>

      {/* Dashboard Invoice Detail Modal */}
      <Modal
        isOpen={showInvoiceDetailModal}
        onClose={() => { setShowInvoiceDetailModal(false); setSelectedDashboardInvoice(null); }}
        title={`Invoice ${selectedDashboardInvoice?.invoice_number || ''}`}
      >
        {selectedDashboardInvoice && (
          <div className="invoice-detail">
            <div className="invoice-detail__header">
              <div>
                <h3 className="invoice-detail__company">POLARIS</h3>
                <p className="invoice-detail__tagline">Professional Detailing</p>
              </div>
              <span className={`badge badge--${selectedDashboardInvoice.status}`}>
                {selectedDashboardInvoice.status}
              </span>
            </div>

            <div className="invoice-detail__info">
              <div>
                <p className="invoice-detail__label">Bill To</p>
                <p className="invoice-detail__value">
                  {selectedDashboardInvoice.first_name} {selectedDashboardInvoice.last_name}
                </p>
              </div>
              <div>
                <p className="invoice-detail__label">Invoice Date</p>
                <p className="invoice-detail__value">
                  {format(new Date(selectedDashboardInvoice.created_at), 'MMMM d, yyyy')}
                </p>
              </div>
            </div>

            {selectedDashboardInvoice.items && selectedDashboardInvoice.items.length > 0 && (
              <table className="table invoice-detail__table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDashboardInvoice.items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.description}</td>
                      <td>{item.quantity}</td>
                      <td>${parseFloat(item.unit_price).toFixed(2)}</td>
                      <td>${parseFloat(item.total).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="invoice-summary">
              <div className="invoice-summary__row invoice-summary__row--total">
                <span>Total</span>
                <span>${parseFloat(selectedDashboardInvoice.total).toFixed(2)}</span>
              </div>
            </div>

            <div className="modal__footer">
              <Link
                to="/invoices"
                className="btn btn--outline"
                onClick={() => { setShowInvoiceDetailModal(false); setSelectedDashboardInvoice(null); }}
              >
                View in Invoices
              </Link>
              <button
                className="btn btn--secondary"
                onClick={() => { setShowInvoiceDetailModal(false); setSelectedDashboardInvoice(null); }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;
